import React, { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { MetricsCard } from "@/components/dashboard/MetricsCard";
import { TimeFilter } from "@/components/dashboard/TimeFilter";
import { CampaignSelector } from "@/components/dashboard/CampaignSelector";
import { CampaignDetailMetrics } from "@/components/dashboard/CampaignDetailMetrics";
import { TopAdsDropdown } from "@/components/dashboard/top-ads/TopAdsDropdown";
import { Users, DollarSign, MousePointer, Eye, TrendingUp, Facebook, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { MetaConnectDialog } from "@/components/integrations/meta-connect";
import { useMeta } from "@/contexts/MetaContext";
import { useMetaMetrics } from "@/hooks/useMetaMetrics";
import { useMetaConnection } from "@/hooks/useMetaConnection";
import { useMetaInsights } from "@/hooks/useMetaInsights";

const Index = () => {
  const [timeFilter, setTimeFilter] = useState<"woche" | "monat" | "gesamt">("woche");
  const [isMetaDialogOpen, setIsMetaDialogOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { isConnected, reconnectToMeta, refreshConnectionStatus } = useMetaConnection();
  const { 
    summary, 
    timeData, 
    isLoading, 
    error,
    refreshSummary 
  } = useMetaInsights(isConnected);
  
  // These are maintained for backward compatibility
  const { selectedCampaignId, setConnected, setAccessToken } = useMeta();
  const metrics = useMetaMetrics(isConnected ? "dummy-token" : null);
  
  // Check if user is admin
  const isAdmin = user?.email?.includes('@admin');

  const handleMetaSuccess = (token: string) => {
    // This is executed after successful Meta connection
    setAccessToken(token);
    setConnected(true);
    setIsMetaDialogOpen(false);
    
    toast({
      title: "Meta verbunden",
      description: "Dein Meta-Konto wurde erfolgreich verbunden.",
      variant: "default",
    });
    
    // Refresh data
    refreshSummary();
  };

  const formatCurrency = (value: number) => {
    return `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };
  
  const formatNumber = (value: number) => {
    return value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toString();
  };

  // The getMetricValue function is now using our new data source
  const getMetricValue = (metricType: 'leads' | 'costs' | 'clicks' | 'impressions') => {
    if (!summary) return 0;
    
    switch (metricType) {
      case 'leads':
        return summary.leads || 0;
      case 'costs':
        return summary.spend || 0;
      case 'clicks':
        return summary.clicks || 0;
      case 'impressions':
        // Assuming impressions are not in the summary, we'll fall back to the previous data
        return getTimeFilter(metrics.impressions);
      default:
        return 0;
    }
  };
  
  // Helper to get values based on time filter from old data structure
  const getTimeFilter = (metric: any) => {
    switch (timeFilter) {
      case "woche": return metric?.thisWeek || 0;
      case "monat": return metric?.thisMonth || 0;
      case "gesamt": return metric?.total || 0;
      default: return metric?.thisWeek || 0;
    }
  };
  
  const getTimeLabel = () => {
    switch (timeFilter) {
      case "woche": return "Diese Woche";
      case "monat": return "Diesen Monat";
      case "gesamt": return "Gesamt";
      default: return "Diese Woche";
    }
  };
  
  return (
    <Layout>
      <div className="flex flex-col w-full h-full">
        <div className="p-6 animate-fade-in">
          <div className="flex flex-col mb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-medium tracking-tight text-[#1f2937] mb-2 flex items-center gap-2">
                  <TrendingUp className="text-[#417F96]" size={24} />
                  <span className="text-[#417F96]">Performance Metriken</span>
                </h2>
                <p className="text-gray-600 leading-relaxed">Übersicht über Ihre aktuellen Leistungsindikatoren</p>
                {isConnected && !isLoading && (
                  <p className="text-xs text-gray-500 mt-1">
                    Letztes Update: {summary?.last_data ? new Date(summary.last_data).toLocaleString() : 'Unbekannt'}
                  </p>
                )}
              </div>
              
              <div className="flex flex-col md:flex-row gap-4">
                {isConnected ? (
                  <div className="flex flex-col md:flex-row gap-4">
                    {isAdmin && (
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline"
                          className="text-white bg-[#417F96] hover:bg-[#3a6f83]"
                          onClick={() => setIsMetaDialogOpen(true)}
                        >
                          <Facebook size={16} className="mr-2" />
                          Meta verbunden
                        </Button>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      {isAdmin && <CampaignSelector />}
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-gray-700 relative"
                        onClick={refreshSummary}
                        disabled={isLoading}
                        title="Daten aktualisieren"
                      >
                        <RefreshCw size={16} className={isLoading ? "animate-spin text-[#417F96]" : "text-[#417F96]"} />
                      </Button>
                    </div>
                  </div>
                ) : isAdmin ? (
                  <Button 
                    onClick={reconnectToMeta}
                    className="bg-[#417F96] hover:bg-[#3a6f83] text-white"
                  >
                    <Facebook size={16} className="mr-2" />
                    Mit Meta verbinden
                  </Button>
                ) : null}
                
                <TimeFilter 
                  activeFilter={timeFilter} 
                  onChange={(filter) => setTimeFilter(filter)} 
                />
              </div>
            </div>
            
            {error && isConnected && (
              <div className="mb-4 p-3 bg-amber-100 text-amber-800 rounded-md flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {!isConnected ? (
              <div className="w-full mb-8">
                <div 
                  className="backdrop-blur-md bg-white/30 rounded-2xl flex flex-col items-start justify-center p-12 w-full shadow-xl"
                  style={{
                    backdropFilter: "blur(12px)",
                    minHeight: "250px",
                  }}
                >
                  <h3 className="text-xl font-semibold tracking-wide text-neutral-800 mb-4">
                    Meta Werbedaten
                  </h3>
                  <p className="text-base text-neutral-600 leading-relaxed mb-6 max-w-md text-left">
                    Deine Werbedaten werden automatisch durch unser Team gepflegt.
                  </p>
                  {isAdmin && (
                    <Button 
                      onClick={reconnectToMeta}
                      className="bg-[#e8f1f9] hover:bg-[#dcecfb] hover:shadow-md text-[#1d4ed8] px-6 py-3 rounded-full text-lg font-medium transition-colors"
                      size="lg"
                    >
                      <Facebook size={20} className="mr-2 text-[#1877F2]" />
                      Meta Ads verbinden
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {isLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-500 mr-3" />
                    <span className="text-lg text-gray-500">Daten werden geladen...</span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <MetricsCard
                        title="Leads"
                        value={formatNumber(getMetricValue('leads'))}
                        period={getTimeLabel()}
                        weekValue={formatNumber(getMetricValue('leads'))}
                        weekPeriod="Diese Woche"
                        chartData={timeData.map(d => d.leads || 0)}
                        chartColor="#417F96"
                        icon={<Users className="w-6 h-6" />}
                        maxValue={Math.max(...timeData.map(d => d.leads || 0)) * 1.2 || 60}
                        labels={timeData.map(d => {
                          const date = new Date(d.date_start);
                          return date.toLocaleDateString('de-DE', { weekday: 'short' });
                        })}
                        chartType="line"
                        isLoading={isLoading}
                      />
                      
                      <MetricsCard
                        title="Werbekosten"
                        value={formatCurrency(getMetricValue('costs'))}
                        period={getTimeLabel()}
                        weekValue={formatCurrency(getMetricValue('costs'))}
                        weekPeriod="Diese Woche"
                        chartData={timeData.map(d => d.spend || 0)}
                        chartColor="#417F96"
                        icon={<DollarSign className="w-6 h-6" />}
                        maxValue={Math.max(...timeData.map(d => d.spend || 0)) * 1.2 || 1200}
                        labels={timeData.map(d => {
                          const date = new Date(d.date_start);
                          return date.toLocaleDateString('de-DE', { weekday: 'short' });
                        })}
                        chartType="line"
                        isLoading={isLoading}
                      />
                      
                      <MetricsCard
                        title="Klicks"
                        value={formatNumber(getMetricValue('clicks'))}
                        period={getTimeLabel()}
                        weekValue={formatNumber(getMetricValue('clicks'))}
                        weekPeriod="Diese Woche"
                        chartData={timeData.map(d => d.clicks || 0)}
                        chartColor="#417F96"
                        icon={<MousePointer className="w-6 h-6" />}
                        maxValue={Math.max(...timeData.map(d => d.clicks || 0)) * 1.2 || 1000}
                        labels={timeData.map(d => {
                          const date = new Date(d.date_start);
                          return date.toLocaleDateString('de-DE', { weekday: 'short' });
                        })}
                        chartType="line"
                        isLoading={isLoading}
                      />
                      
                      <MetricsCard
                        title="Impressions"
                        value={formatNumber(getMetricValue('impressions'))}
                        period={getTimeLabel()}
                        weekValue={formatNumber(getMetricValue('impressions'))}
                        weekPeriod="Diese Woche"
                        chartData={metrics.impressions.byDay}
                        chartColor="#417F96"
                        icon={<Eye className="w-6 h-6" />}
                        maxValue={Math.max(...metrics.impressions.byDay) * 1.2 || 100}
                        labels={["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]}
                        chartType="donut"
                        isLoading={isLoading}
                      />
                    </div>
                    
                    {isAdmin && selectedCampaignId && metrics.campaignDetails && (
                      <div className="mt-6 mb-6">
                        <CampaignDetailMetrics 
                          campaignDetails={metrics.campaignDetails}
                          isLoading={isLoading}
                        />
                      </div>
                    )}
                    
                    <TopAdsDropdown />
                  </>
                )}
              </>
            )}
          </div>
        </div>
        
        {isAdmin && (
          <MetaConnectDialog 
            open={isMetaDialogOpen} 
            onOpenChange={setIsMetaDialogOpen}
            onSuccess={handleMetaSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default Index;
